/**
 * **Die zwei Auszeichnungen, die vorher an der Spalte standen.**
 *
 * `DataTable` beschreibt eine Zeile seit #142 als Sammlung **fertiger Zellen**
 * und nicht mehr als Datensatz, aus dem die Spalte sich mit `render` bedient.
 * Damit fallen `strong` und `numeric` an der Spalte weg — der Baukasten des
 * Skills kennt beides nicht, und er braucht es auch nicht: Eine Zelle ist ein
 * `ReactNode` und trägt ihre Auszeichnung selbst.
 *
 * Vierzig Spalten trugen eines von beiden. Damit die Auszeichnung nicht
 * vierzigmal neu entsteht, steht sie hier einmal.
 *
 * **Das sind keine nachgebauten Bausteine.** ADR-0025 §2 und `design/VENDOR.md`
 * verbieten, dem Design-System auf eigene Faust etwas hinzuzufügen; was hier
 * steht, ist etwas anderes — zwei Aufrufhilfen des Produkts, gebaut aus zwei
 * Token und drei CSS-Eigenschaften, die genau das tun, was der abgelöste
 * Baustein an der Spalte tat. Sie treten an keiner Stelle an die Stelle eines
 * Bausteins, den der Skill führt.
 */

import type { ReactElement, ReactNode } from "react";

/**
 * Die **tragende Zelle** einer Zeile: die Spalte, an der man die Zeile
 * wiedererkennt — der Name, die Kennung, der Pfad, der Titel.
 *
 * Sie steht auf `--fg-1`, während die Zellen daneben auf `--fg-4` stehen. In den
 * meisten Listen ist die tragende Spalte die erste, und die hebt `DataTable`
 * ohnehin an; in zweien steht sie an zweiter Stelle (die Regel des Ausdrucks in
 * der Ticketverwaltung, der Agent am festgefahrenen Prüfauftrag), und dort ist
 * diese Hülle der ganze Unterschied.
 *
 * `title` nimmt sie entgegen, weil die tragende Spalte fast immer die schmalste
 * mit dem längsten Inhalt ist: Sie bricht hinten ab und steht ganz im Titel.
 * Ohne das stünde an jeder solchen Fundstelle wieder ein eigenes `<span>` darum.
 */
export function StrongCell({
  children,
  title,
}: {
  children: ReactNode;
  title?: string;
}): ReactElement {
  return (
    <span title={title} style={{ color: "var(--fg-1)" }}>
      {children}
    </span>
  );
}

/**
 * Die **Zahlenzelle**: rechtsbündig, dicktengleich, mit tabellarischen Ziffern.
 *
 * Zahlen untereinander werden verglichen und nicht gelesen — sie stehen deshalb
 * stellengerecht. `tabular-nums` hält die Ziffern auf gleicher Breite,
 * `var(--font-mono)` setzt sie in dieselbe Schrift wie den Code (#107): Wo eine
 * Zahl eine Größe benennt und keinen Satz, ist die dicktengleiche Schrift die
 * genauere.
 *
 * Ein **Block** und keine Inline-Hülle: An einer Inline-Hülle liefe
 * `text-align` ins Leere, und die Ausrichtung stünde wieder allein an der Spalte
 * — also an derselben Stelle, von der sie hierher gezogen ist.
 */
export function NumericCell({ children }: { children: ReactNode }): ReactElement {
  return (
    <span
      style={{
        display: "block",
        textAlign: "right",
        fontFamily: "var(--font-mono)",
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {children}
    </span>
  );
}
